import { Avatar, Spinner } from "@nextui-org/react";
import { IconCamera } from "@tabler/icons-react";
import React, { FC, useRef, useState } from "react";
import {
  useInvalidate,
  useNotification,
  useUpdate,
} from "@refinedev/core";
import { useActiveAssociation } from "../../context/active-association.context";
import { useFileEntityUrl } from "../../hooks";
import { ApiSdk } from "../../lib/api-sdk";
import { ResourceIdentifier } from "../../resources";
import { AssociationUser, User } from "../../sdk";
import { CropperModal } from "./cropper-modal";
import { getAvatarInitials } from "../../lib/utils";

interface ProfileImageUploadProps {
  associationUser: AssociationUser | undefined;
}

export const ProfileImageUpload: FC<ProfileImageUploadProps> = ({
  associationUser,
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeAssociation = useActiveAssociation();
  const { mutateAsync: updateUser } = useUpdate<Partial<User>>();
  const invalidate = useInvalidate();
  const { open } = useNotification();
  const imageUrl = useFileEntityUrl(associationUser?.user?.photoId);

  const handleAvatarClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      open?.({
        message: "Invalid file type",
        description: "Please select an image file.",
        type: "error",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      open?.({
        message: "File too large",
        description: "Please select an image under 10MB.",
        type: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setImage(reader.result as string);
      setIsCropperOpen(true);
    });
    reader.readAsDataURL(file);

    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  function dataUrlToFile(dataUrl: string): File {
    const arr = dataUrl.split(",");
    const mime = arr[0]?.match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], "profile-image.png", { type: mime });
  }

  const handleCroppedImage = async (croppedDataUrl: string) => {
    if (!associationUser?.id || !associationUser?.user?.id) return;

    setIsUploading(true);
    try {
      const file = dataUrlToFile(croppedDataUrl);
      const fileEntity = await ApiSdk.files.uploadFile({
        formData: { file },
      });

      await updateUser(
        {
          resource: ResourceIdentifier.AssociationUser,
          meta: { associationId: activeAssociation?.id },
          id: associationUser.id,
          values: {
            user: {
              id: associationUser.user.id,
              photo: fileEntity.id,
            },
          } as any,
          successNotification: {
            message: "Profile image updated",
            type: "success",
          },
        },
        {
          onSettled: async () => {
            setIsUploading(false);
            await invalidate({
              resource: ResourceIdentifier.AssociationUser,
              invalidates: ["all"],
            });
            await invalidate({
              resource: ResourceIdentifier.User,
              invalidates: ["all"],
            });
          },
          onError: (e) => {
            open?.({
              message: "Error updating profile image",
              description: e?.response?.data?.message,
              type: "error",
            });
          },
        }
      );
    } catch (e) {
      setIsUploading(false);
      open?.({
        message: "Error uploading image",
        type: "error",
      });
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      {isCropperOpen && image && (
        <CropperModal
          isOpen={isCropperOpen}
          image={image}
          onOpenChange={setIsCropperOpen}
          onSetCroppedImage={handleCroppedImage}
        />
      )}
      <button
        type="button"
        onClick={handleAvatarClick}
        className="group relative cursor-pointer rounded-full"
        disabled={isUploading}
      >
        <Avatar
          className="h-24 w-24 lg:h-32 lg:w-32 border-4 border-secondary bg-secondary text-light text-md"
          name={`${associationUser?.user?.firstName} ${associationUser?.user?.lastName}`}
          getInitials={getAvatarInitials}
          src={imageUrl}
        />
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 transition-all group-hover:bg-black/40">
          {isUploading ? (
            <Spinner size="lg" color="white" />
          ) : (
            <IconCamera
              className="text-white opacity-0 transition-opacity group-hover:opacity-100"
              size={32}
            />
          )}
        </div>
      </button>
    </>
  );
};
